import { useState, useEffect } from 'react'
import { LayoutGrid, Loader2, X, Search } from 'lucide-react'
import { workspaceApi, type WorkspaceListItem } from '@papan/workspace/client'
import { createPortal } from 'react-dom'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { pathMatchesFilter } from './fileFilter'

interface WorkspacePickerDialogProps {
  currentFolderId: string
  onSelect: (folderId: string) => void
  onClose: () => void
}

export function WorkspacePickerDialog({ currentFolderId, onSelect, onClose }: WorkspacePickerDialogProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  const [workspaces, setWorkspaces] = useState<WorkspaceListItem[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('')

  useEffect(() => {
    workspaceApi.list().then(items => {
      // Sort by updatedAt desc
      const sorted = items.sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''))
      setWorkspaces(sorted)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  const lowerFilter = filter.toLowerCase()
  const anchored = lowerFilter.startsWith('/')
  const filtered = filter
    ? workspaces.filter(w =>
        pathMatchesFilter(w.name.toLowerCase(), lowerFilter, false) ||
        pathMatchesFilter(w.path.toLowerCase(), lowerFilter, anchored)
      )
    : workspaces

  const handleConfirm = () => {
    if (selectedId) onSelect(selectedId)
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className={`${themeClass} ws-glass-modal ws-anim-modal-in relative w-[400px] max-h-[500px] flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b themed-border">
          <div className="flex items-center gap-2">
            <LayoutGrid size={16} className="themed-text-secondary" />
            <h3 className="text-sm font-medium themed-text">Open Project</h3>
          </div>
          <button onClick={onClose} className="themed-text-muted hover:themed-text">
            <X size={16} />
          </button>
        </div>

        {/* Search */}
        <div className="px-3 py-2 border-b themed-border">
          <div className="flex items-center gap-2 themed-bg-tertiary rounded px-2 py-1.5">
            <Search size={14} className="themed-text-muted shrink-0" />
            <input
              type="text"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Filter workspaces..."
              className="flex-1 text-sm bg-transparent themed-text outline-none placeholder:themed-text-muted"
              autoFocus
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-auto p-2 min-h-[200px]">
          {loading ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 size={16} className="animate-spin themed-text-muted" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-sm themed-text-muted text-center py-8">
              {filter ? 'No matching projects' : 'No projects found'}
            </div>
          ) : (
            filtered.map(ws => {
              const isCurrent = ws.folderId === currentFolderId
              const isSelected = ws.folderId === selectedId
              return (
                <button
                  key={ws.id}
                  onClick={() => setSelectedId(ws.folderId)}
                  onDoubleClick={() => { if (!isCurrent) onSelect(ws.folderId) }}
                  className={`flex items-start gap-2 w-full text-left py-1.5 px-2 rounded transition-colors ${
                    isSelected
                      ? 'bg-blue-600/30 themed-text'
                      : isCurrent
                      ? 'themed-text-muted'
                      : 'themed-text-secondary hover:themed-text hover:themed-bg-tertiary'
                  }`}
                >
                  <LayoutGrid size={24} className={`mt-0.5 ${isCurrent ? 'text-purple-400 shrink-0' : 'themed-text-muted shrink-0'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm truncate">{ws.name}</span>
                      {isCurrent && <span className="text-[10px] themed-text-muted shrink-0">(current)</span>}
                    </div>
                    <div className="text-[11px] themed-text-muted truncate">{ws.path}</div>
                  </div>
                </button>
              )
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-4 py-3 border-t themed-border gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm themed-text-secondary hover:themed-text rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedId || selectedId === currentFolderId}
            className="px-3 py-1.5 text-sm themed-btn-primary rounded transition-colors disabled:opacity-50"
          >
            Open
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
